import React from 'react';
import {
  Button,
  Spacing,
  Typography,
  Progress,
  StyledDiv,
  Icon,
} from '@taleemabad/dsm/components';
import { CardContainer } from '../card/card-container';
import styled from '@emotion/styled';
import { BreakpointSizes, mq } from '@taleemabad/dsm/themes';
import { formatUtcDate } from '@taleemabad/shared';

export interface ICoreLessonPlan {
  id: string;
  grade: string;
  subject: string;
  topic: string;
  pageNumber?: string;
  createdAt: string;
  status?: 'READY' | 'PENDING';
  revisionLp?: boolean;
  completedAt?: string | null;
}

export enum LessonPlanStatus {
  READY = 'READY',
  PENDING = 'PENDING',
}

interface CoreLessonPlanCardProps {
  lessonPlan: ICoreLessonPlan;
  onViewLessonPlan: (lessonPlan: ICoreLessonPlan) => void;
  getSubjectDisplayName?: (subject: string) => string;
}

const getGradeBadgeColor = (grade: string): string => {
  const gradeNum = Number.parseInt(grade);

  const gradeColorMap: Record<number, string> = {
    1: '--md-sys-color-schemes-error-container',
    2: '--md-ref-secondary-70',
    3: '--md-sys-color-intermediate-level',
    4: '--md-sys-color-light-primary-container',
    5: '--md-sys-color-secondary-fixed',
  };

  return gradeColorMap[gradeNum] || '--md-sys-color-outline';
};

export const CoreLessonPlanCard: React.FC<CoreLessonPlanCardProps> = ({
  lessonPlan,
  onViewLessonPlan,
  getSubjectDisplayName,
}) => {
  const defaultGetSubjectDisplayName = (subject: string) => {
    if (!subject) return 'Unknown Subject';

    const subjectMap: Record<string, string> = {
      Eng: 'English',
      Urdu: 'Urdu',
      Maths: 'Maths',
      Science: 'Science',
    };

    return (
      subjectMap[subject] ??
      subject.charAt(0).toUpperCase() + subject.slice(1).toLowerCase()
    );
  };

  const displaySubjectName =
    getSubjectDisplayName ?? defaultGetSubjectDisplayName;

  return (
    <CardContainer variant="outline" minHeight="auto">
      <ContainerWrapper>
        <StyledDiv
          justifyContent="space-between"
          alignItem="center"
          gap={8}
          marginBottom={12}
        >
          <StyledDiv flexDirection="row" alignItem="center" gap={8}>
            <Typography variant="textTitleMediumSurface" ellipseLine={1}>
              {displaySubjectName(lessonPlan.subject)}
            </Typography>
            {lessonPlan.completedAt && (
              <CompletedBadge>
                <Icon name="check_circle" size="tiny" iconFill={true} />
                <Typography
                  variant="textLabelSmall"
                  color="var(--md-sys-color-on-tertiary-container)"
                >
                  Completed
                </Typography>
              </CompletedBadge>
            )}
            {lessonPlan.revisionLp && (
              <StyledDiv
                background="--md-sys-color-tertiary-container"
                borderRadius="--rk-spacing-xxl"
                padding="var(--rk-spacing-xs) var(--rk-spacing-md)"
                whiteSpace="nowrap"
                alignItem="center"
                justifyContent="center"
              >
                <Typography
                  variant="textLabelSmall"
                  color="var(--md-sys-color-on-tertiary-container)"
                >
                  Revision
                </Typography>
              </StyledDiv>
            )}
          </StyledDiv>
          <StyledDiv
            background={getGradeBadgeColor(lessonPlan.grade)}
            borderRadius="--rk-spacing-xxl"
            padding="var(--rk-spacing-md) var(--rk-spacing-xl)"
            whiteSpace="nowrap"
            alignItem="center"
            justifyContent="center"
          >
            <Typography
              variant="textLabelMediumSurface"
              color="var(--md-ref-neutral-20)"
            >
              Grade {lessonPlan.grade}
            </Typography>
          </StyledDiv>
        </StyledDiv>

        <StyledDiv
          flexDirection="column"
          justifyContent="space-between"
          height="100%"
        >
          <StyledDiv flexDirection="column">
            <ContentRow>
              {lessonPlan.pageNumber?.trim() && (
                <Typography variant="textBodyMediumSurface">
                  {`Book Page: ${lessonPlan.pageNumber}`}
                </Typography>
              )}
            </ContentRow>

            <ContentRow>
              {lessonPlan.topic?.trim() && (
                <Typography variant="textBodyMediumSurface" ellipseLine={2}>
                  {`Topic: ${lessonPlan.topic}`}
                </Typography>
              )}
            </ContentRow>
          </StyledDiv>

          <StyledDiv flexDirection="column">
            <Spacing marginTop="lg" marginBottom="xl">
              <Typography variant="textLabelLargeSecondary">
                Created: {formatUtcDate(lessonPlan.createdAt)}
              </Typography>
            </Spacing>

            <Button
              variant="outlined"
              onClick={() => onViewLessonPlan(lessonPlan)}
              disabled={lessonPlan.status === LessonPlanStatus.PENDING}
              width="100%"
            >
              {lessonPlan.status === LessonPlanStatus.PENDING ? (
                <StyledDiv flexDirection="row" alignItem="center" gap={8}>
                  <Progress type="circular" indeterminate={true} size="small" />
                  Generating...
                </StyledDiv>
              ) : (
                'Open Lesson'
              )}
            </Button>
          </StyledDiv>
        </StyledDiv>
      </ContainerWrapper>
    </CardContainer>
  );
};

const ContainerWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 180px;
  padding: var(--rk-spacing-lg);

  ${mq(BreakpointSizes.xl)} {
    min-height: 172px;
  }
`;

const ContentRow = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: var(--rk-spacing-sm);
  min-height: 24px;
`;

const CompletedBadge = styled.div`
  display: flex;
  align-items: center;
  gap: var(--rk-spacing-xs);
  background: var(--md-sys-color-tertiary-container);
  border-radius: var(--rk-spacing-xxl);
  padding: var(--rk-spacing-xs) var(--rk-spacing-md);
  white-space: nowrap;
  color: var(--md-sys-color-on-tertiary-container);
`;
